import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
const STORAGE_KEY = "tb.health-banner.dismissed-at";
// Visual mapping per severity. critical = red+blink, urgent = orange,
// warn = yellow, info = gray (collapsed by default).
const SEVERITY_STYLES = {
    critical: { bg: "#dc2626", label: "Critical", icon: "✕" },
    urgent: { bg: "#ea580c", label: "Urgent", icon: "⚠" },
    warn: { bg: "#d97706", label: "Warning", icon: "⚠" },
    info: { bg: "#6b7280", label: "Info", icon: "ℹ" },
    pass: { bg: "#10b981", label: "OK", icon: "✓" },
    skip: { bg: "#9ca3af", label: "Skip", icon: "—" },
};
function ageLabel(since) {
    if (!since)
        return "";
    const ts = new Date(since).getTime();
    if (Number.isNaN(ts))
        return "";
    const ageMs = Date.now() - ts;
    const ageH = ageMs / 3_600_000;
    if (ageH < 1)
        return "<1h";
    if (ageH < 24)
        return `${Math.floor(ageH)}h`;
    return `${Math.floor(ageH / 24)}d`;
}
export default function HealthBanner() {
    const [state, setState] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [acking, setAcking] = useState(null);
    async function load() {
        try {
            const data = await api.healthBanner();
            setState(data);
        }
        catch {
            // Silently swallow — banner is best-effort.
        }
    }
    useEffect(() => {
        load();
        const t = setInterval(load, 60_000);
        return () => clearInterval(t);
    }, []);
    // #191 Tuning 1: 1-click ack from the banner. Asks for TTL + reason
    // via simple prompt to keep the chrome lightweight; full ack-manager
    // lives on /health for revoke + history.
    async function ackInline(checkName, detailHash) {
        const ttlChoice = prompt(`Ack "${checkName}"\n\nTTL hours? (e.g. 1, 24, 168 — empty = permanent)`, "24");
        if (ttlChoice === null)
            return; // user cancelled
        const ttl = ttlChoice.trim() === "" ? null : Number(ttlChoice);
        if (ttl !== null && (Number.isNaN(ttl) || ttl <= 0)) {
            alert("Invalid TTL");
            return;
        }
        const reason = prompt("Reason (required):");
        if (!reason || !reason.trim())
            return;
        try {
            setAcking(checkName);
            await api.healthAckCreate(checkName, detailHash, reason.trim(), ttl, "team");
            await load();
        }
        catch (e) {
            alert(`Ack failed: ${String(e)}`);
        }
        finally {
            setAcking(null);
        }
    }
    if (!state)
        return null;
    // No prominent display for pass / skip / info-only state.
    // Info items live on /health, never as a chrome-banner.
    if (state.status_overall === "pass" || state.status_overall === "skip") {
        return null;
    }
    if (state.status_overall === "info" || state.warnings.length === 0) {
        return null;
    }
    const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) || "0");
    const checkedAtMs = new Date(state.checked_at).getTime();
    // Auto-undismiss when state-snapshot is newer than dismissal time.
    if (dismissedAt > 0 && checkedAtMs <= dismissedAt && collapsed) {
        const sty = SEVERITY_STYLES[state.status_overall];
        return (_jsxs("button", { type: "button", onClick: () => {
                setCollapsed(false);
                localStorage.removeItem(STORAGE_KEY);
            }, style: {
                display: "block",
                width: "100%",
                padding: "0.25rem 0.75rem",
                background: sty.bg,
                color: "white",
                border: "none",
                fontSize: "0.75rem",
                textAlign: "left",
                cursor: "pointer",
            }, children: [sty.icon, " ", state.warnings.length, " health issue(s) \u2014 click to expand", state.counts.acked > 0 && ` (+${state.counts.acked} acked)`] }));
    }
    const sty = SEVERITY_STYLES[state.status_overall];
    // Counts breakdown for the title.
    const breakdown = [];
    if (state.counts.critical > 0)
        breakdown.push(`${state.counts.critical} critical`);
    if (state.counts.urgent > 0)
        breakdown.push(`${state.counts.urgent} urgent`);
    if (state.counts.warn > 0)
        breakdown.push(`${state.counts.warn} warn`);
    const breakdownText = breakdown.join(", ");
    return (_jsxs("div", { role: "alert", style: {
            background: sty.bg,
            color: "white",
            padding: "0.5rem 0.75rem",
            fontSize: "0.85rem",
            borderBottom: "1px solid rgba(0,0,0,0.1)",
        }, children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [_jsxs("strong", { style: { marginRight: "0.5rem" }, children: [sty.icon, " ", sty.label, " \u2014 ", breakdownText, state.counts.acked > 0 && (_jsxs("span", { style: { opacity: 0.85, marginLeft: 6, fontWeight: "normal" }, children: ["(+", state.counts.acked, " acked)"] }))] }), _jsxs("div", { style: { display: "flex", gap: "0.5rem" }, children: [_jsx(Link, { to: "/health", style: { color: "white", textDecoration: "underline", fontSize: "0.8rem" }, children: "Open Health page \u2192" }), _jsx("button", { type: "button", onClick: () => {
                                    setCollapsed(true);
                                    localStorage.setItem(STORAGE_KEY, String(Date.now()));
                                }, style: {
                                    background: "transparent",
                                    color: "white",
                                    border: "1px solid rgba(255,255,255,0.4)",
                                    borderRadius: 3,
                                    padding: "0 0.4rem",
                                    cursor: "pointer",
                                    fontSize: "0.75rem",
                                }, title: "Hide banner until next probe-state change", children: "\u00D7" })] })] }), _jsxs("ul", { style: { margin: "0.4rem 0 0 1.2rem", padding: 0, fontSize: "0.8rem" }, children: [state.warnings.slice(0, 6).map((w) => {
                        const age = ageLabel(w.since);
                        const itemSty = SEVERITY_STYLES[w.level];
                        return (_jsxs("li", { style: { marginBottom: 2 }, children: [_jsxs("span", { style: {
                                        background: w.level !== state.status_overall ? itemSty.bg : "rgba(0,0,0,0.25)",
                                        padding: "0 4px",
                                        borderRadius: 2,
                                        marginRight: 4,
                                        fontSize: "0.7rem",
                                        fontWeight: "bold",
                                    }, title: w.escalation > 0 ? `escalated ${w.escalation}x from ${w.base_level}` : "", children: [w.level.toUpperCase(), w.escalation > 0 && "↑".repeat(w.escalation)] }), _jsx("code", { style: { background: "rgba(0,0,0,0.18)", padding: "0 4px", borderRadius: 2 }, children: w.id }), " ", w.message, age && (_jsxs("span", { style: { opacity: 0.85, marginLeft: 6 }, children: ["(", age, ")"] })), _jsx("button", { type: "button", disabled: acking === w.id, onClick: () => ackInline(w.id, w.detail_hash), title: "Acknowledge \u2014 silence this issue with TTL + reason", style: {
                                        marginLeft: 8,
                                        fontSize: "0.7rem",
                                        padding: "0 6px",
                                        background: "rgba(255,255,255,0.18)",
                                        color: "white",
                                        border: "1px solid rgba(255,255,255,0.4)",
                                        borderRadius: 3,
                                        cursor: "pointer",
                                    }, children: acking === w.id ? "…" : "🔕 ack" })] }, w.id));
                    }), state.warnings.length > 6 && (_jsxs("li", { style: { opacity: 0.85 }, children: ["\u2026 +", state.warnings.length - 6, " more on", " ", _jsx(Link, { to: "/health", style: { color: "white" }, children: "Health" })] }))] }), state.acked.length > 0 && (_jsxs("div", { style: { marginTop: "0.4rem", fontSize: "0.75rem", opacity: 0.9 }, children: [state.acked.length, " acked: ", state.acked.slice(0, 3).map(a => a.id).join(", "), state.acked.length > 3 && ` +${state.acked.length - 3}`] }))] }));
}
